import { InjectionToken } from '@angular/core';
import type { TranslationSegment } from '@core/translation/translation-provider';

/**
 * Voice translation — architecture §8.9.
 *
 * One contract for every model: give it the speaker's audio, get back
 * translated audio. Sample rates, stream or batch and chunk size belong to the
 * engine, not to its callers, so a model change never reaches the track swap
 * (production) or the `getUserMedia` substitution (tests) that consume
 * `output`.
 *
 * Deliberately separate from `TranslationProvider`. That interface returns text
 * and is shaped so it *cannot* reach the outbound audio (D6). This one produces
 * the outbound audio by design (D32) — for the microphone only, never the
 * stethoscope.
 */
export interface VoiceEngine {
  readonly name: string;
  start(input: MediaStream, options: VoiceEngineOptions): Promise<VoiceEngineSession>;
}

export interface VoiceEngineOptions {
  /** BCP-47. What the speaker speaks; a model may detect it instead. */
  sourceLanguage: string;
  /** BCP-47. What the listener wants to hear. */
  targetLanguage: string;
  /** Transcripts, where the model provides them. */
  onText?(segment: TranslationSegment): void;
  /** Translated audio arrived. Called per chunk, so it must be cheap. */
  onAudio?(): void;
  /**
   * The speaker's microphone carries speech, judged locally. Called per input
   * frame, so it must be cheap. The model's own transcript of what it heard
   * arrives only a moment before its audio, so it cannot say a translation is
   * on the way — this can, the instant someone starts talking.
   */
  onSpeech?(): void;
  /** Playout health, about once a second, where the engine plays audio itself. */
  onPlayout?(stats: PlayoutStats): void;
  /**
   * The engine stopped producing audio. Reported, never handled here — the
   * caller decides whether the call falls back to the live microphone.
   * Called at most once per session.
   */
  onError(reason: string): void;
}

export interface VoiceEngineSession {
  /**
   * Translated voice. Live for the whole session: silence between utterances
   * and after a failure, never ended before `stop()`. An ended track is
   * published without any error and the far side hears nothing (D24, C11).
   */
  readonly output: MediaStream;
  /**
   * Resolves once everything said so far has been translated and played into
   * `output` — or the model has evidently dropped it, or `signal` aborts, or
   * the session ends. Lets a mute silence the translation only after the
   * sentence the speaker finished before muting has been heard.
   */
  whenDrained(signal: AbortSignal): Promise<void>;
  /** Releases the input graph, the model connection and the output. Idempotent. */
  stop(): Promise<void>;
}

export interface PlayoutStats {
  /** Samples waiting to play. */
  buffered: number;
  /** Bursts that ran dry; roughly one per sentence when delivery is healthy. */
  drains: number;
  /** Times audio was dropped because the buffer was full. Should stay 0. */
  overruns: number;
}

/** `null` when no voice engine is configured; the control is then not offered. */
export const VOICE_ENGINE = new InjectionToken<VoiceEngine | null>('VOICE_ENGINE');
